import { JSONContent } from '@tiptap/core';

import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { CsInSchoolsLogo } from 'approot/shared/authors/cs-in-schools';
import { useCsInSchool } from 'approot/shared/authors/authors.hooks';
import { ActivityBuilderIntroTitle } from './activity-builder-intro';
import * as Styled from './activity-builder-intro.style';
import DismissableSnackbar from 'approot/shared/snackbar-notification/dismissable-snackbar';
import { useIntroStudentSnackbar } from './use-intro-student-snackbar';
import { openNewTab } from 'approot/shared/logic/open-new-tab';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import { PRIVATE_MY_STUDENTS } from 'global/private-routes.constants';
import { purple1, purple7 } from 'approot/shared/styles/colours.style';
import IconExpand from 'approot/shared/icons/icon-expand';
import ImageEmojiLightbulb from 'approot/shared/images/emojis/image-emoji-lightbulb';
import { ImageEmojiWrapper } from 'approot/shared/images/emojis/image-emoji-style';
import { useActivityBuilderPresentContext } from '../present/activity-builder-present.context';
import { addStudentsPushDataTrackingEvent } from 'approot/teacher-student/logic/add-students-push-data-tracking-event';
import { EVENT_TARGET_TYPE_CTA } from 'approot/shared/data-tracker/data-events.constants';
import type { Pluck } from 'lib/gql/pluck';

const ActivityBuilderIntroductionView = ({
  focusQuestion,
  phenomena,
  authorId,
  hasBanner,
  assessmentName,
}: {
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  assessmentName?: JSONContent | null;
  authorId?: number;
  hasBanner?: boolean;
}) => {
  const { isCsInSchoolsLesson } = useCsInSchool(authorId);
  const { shouldShow, handleDismiss, snackbarRef } = useIntroStudentSnackbar();
  const { isLesson } = useActivityBuilderPresentContext();

  return (
    <>
      <Styled.IntroSlide $hasBanner={hasBanner}>
        <ActivityBuilderIntroTitle
          richtext={focusQuestion?.value || phenomena?.value || assessmentName}
          logo={
            isCsInSchoolsLesson && <CsInSchoolsLogo justifyContent="center" />
          }
        />
      </Styled.IntroSlide>

      <div ref={snackbarRef}>
        <DismissableSnackbar
          isVisible={shouldShow}
          onDismiss={handleDismiss}
          backgroundColor={purple1}
          textColor={purple7}
          data-selector="intro-student-snackbar"
          actions={
            <Styled.IntroAddStudentsButton
              to={getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))}
              aria-label="Add Students - opens in a new tab"
              data-testid="btn-add-students"
              onClick={e => {
                e.preventDefault();
                addStudentsPushDataTrackingEvent({
                  targetType: EVENT_TARGET_TYPE_CTA,
                  targetIdentifier: 'activity-builder-intro',
                });
                openNewTab(
                  getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))
                );
              }}
            >
              Add Students
              <IconExpand width={24} height={24} />
            </Styled.IntroAddStudentsButton>
          }
        >
          <ImageEmojiWrapper>
            <ImageEmojiLightbulb width={16} />
          </ImageEmojiWrapper>{' '}
          Capture Student Responses in your{' '}
          {isLesson ? 'Lessons' : 'Assessments'}
        </DismissableSnackbar>
      </div>
    </>
  );
};

export default ActivityBuilderIntroductionView;
